import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

import { supabase } from "../../supabaseClient";
import { useDriverDetail } from "../../hooks/queries";
import HeaderContainer from "../CrewDashboard/HeaderContainer";
import TextInput from "../common/TextInput";

const DriverDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const { id: driverID } = useParams();
  const { data: driverDetail, isSuccess: successDriverData } =
    useDriverDetail(driverID);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      dob: "",
      phone: "",
      email: "",
      password: "",
      address: "",
    },
  });

  useEffect(() => {
    // Check session storage for user authentication
    const userId = sessionStorage.getItem("UserId");
    const userEmail = sessionStorage.getItem("User");
    const isAuthenticated = sessionStorage.getItem("isUserAuthenticated");
    
    if (userId && userEmail && isAuthenticated === "true") {
      console.log("User is signed in from session:", { userId, userEmail });
    } else {
      console.log("No valid session found in storage");
    }
  }, []);

  useEffect(() => {
    if (successDriverData && driverDetail) {
      reset({
        firstName: driverDetail.FirstName || "",
        lastName: driverDetail.LastName || "",
        dob: driverDetail.DOB || "",
        phone: driverDetail.Phone || "",
        email: driverDetail.Email || "",
        password: driverDetail.Password || "",
        address: driverDetail.Address || "",
      });
      setPhotoPreview(driverDetail.AvatarUrl || null);
    }
  }, [driverDetail, successDriverData, reset]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePhoto(file);
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreview(previewUrl);
    }
  };

  const onSuccess = useCallback(() => {
    toast.success("Driver saved successfully!");
    // Navigate directly to the appropriate dashboard instead of credentials screen
    const userRole = sessionStorage.getItem("UserRole");
    if (userRole === "1") {
      navigate("/super-admin"); // Admin goes to super admin dashboard
    } else {
      navigate("/manager-drivers"); // Manager goes to their drivers list
    }
  }, [navigate]);

  const onError = useCallback((error) => {
    console.error("Error saving driver:", error);
    toast.error("Failed to save driver. Check console for details.");
  }, []);

  const handleSave = useCallback(
    async (formData) => {
      try {
        setLoading(true);

        // 1. Upload photo to Supabase Storage
        let photoUrl = null;
        if (profilePhoto) {
          const fileExt = profilePhoto.name.split(".").pop();
          const fileName = `${Date.now()}.${fileExt}`;
          const filePath = `driver-photos/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from("driver-photos")
            .upload(filePath, profilePhoto);

          if (uploadError) throw uploadError;

          const { data } = supabase.storage
            .from("driver-photos")
            .getPublicUrl(filePath);
          photoUrl = data.publicUrl;
        }

        // 2. Insert/Update data into Supabase tables
        if (driverID) {
          // Update existing driver
          const { data: driverAuth, err: driverAuthErr } = await supabase
            .from("driver_auth")
            .select()
            .eq("id", driverID);

          if (driverAuthErr) throw driverAuthErr;

          if (driverAuth && driverAuth.length > 0) {
            // Update driver_auth
            const { error: authUpdateErr } = await supabase
              .from("driver_auth")
              .update({
                email: formData.email,
                password_hash: formData.password,
              })
              .eq("id", driverID);

            if (authUpdateErr) throw authUpdateErr;

            // Update driver_profiles
            // Get current manager's UserID to maintain assignment
            const currentManagerId = parseInt(sessionStorage.getItem("UserId"));
            if (!currentManagerId) {
              throw new Error("Manager session not found. Please log in again.");
            }
            
            const { error: profileUpdateErr } = await supabase
              .from("driver_profiles")
              .upsert({
                auth_id: driverID,
                first_name: formData.firstName,
                last_name: formData.lastName,
                phone: formData.phone,
                hire_date: formData.dob,
                avatar_url: photoUrl || driverDetail?.AvatarUrl,
                manager_id: currentManagerId, // Keep assigned to current manager
              });

            if (profileUpdateErr) throw profileUpdateErr;

            // Update Drivers table
            const { error: driversUpdateErr } = await supabase
              .from("Drivers")
              .update({
                FirstName: formData.firstName,
                LastName: formData.lastName,
                Email: formData.email,
                Phone: formData.phone,
                Address: formData.address,
                HireDate: formData.dob,
                DriverPhoto: photoUrl || driverDetail?.AvatarUrl,
                Active: true,
                updated_at: new Date().toISOString(),
              })
              .eq("auth_id", driverID);

            if (driversUpdateErr) throw driversUpdateErr;
          }

          // For updates, get the numeric DriverID from the Drivers table
          const { data: driverData } = await supabase
            .from("Drivers")
            .select("DriverID")
            .eq("auth_id", driverID)
            .single();

          dispatch({ type: "DRIVER_ID", payload: driverData?.DriverID || driverID });
          onSuccess();
        } else {
          // Create new driver
          let driverAuth;
          const { data: oldDriverAuth } = await supabase
            .from("driver_auth")
            .select()
            .eq("email", formData.email);
          if (oldDriverAuth && oldDriverAuth.length > 0) {
            driverAuth = oldDriverAuth;
          } else {
            const { data: _driverAuth, error: driverAuthErr } = await supabase
              .from("driver_auth")
              .insert({
                email: formData.email,
                password_hash: formData.password,
                is_active: true,
              })
              .select();

            if (driverAuthErr) throw driverAuthErr;
            driverAuth = _driverAuth;

            // Get current manager's UserID for automatic assignment
            const currentManagerId = parseInt(sessionStorage.getItem("UserId"));
            if (!currentManagerId) {
              throw new Error("Manager session not found. Please log in again.");
            }
            
            const { error: _driverProfileErr } = await supabase
              .from("driver_profiles")
              .insert({
                auth_id: driverAuth[0].id,
                first_name: formData.firstName,
                last_name: formData.lastName,
                phone: formData.phone,
                hire_date: formData.dob,
                avatar_url: photoUrl,
                manager_id: currentManagerId, // Automatically assign to current manager
              });

            if (_driverProfileErr)
              throw new Error("Error on driver profile creation");
          }

          // Create Drivers table record and get the numeric ID
          console.log("DriverNew - About to insert into Drivers table");
          const { data: driversData, error: driversInsertErr } = await supabase
            .from("Drivers")
            .insert({
              auth_id: driverAuth[0].id,
              FirstName: formData.firstName,
              LastName: formData.lastName,
              Email: formData.email,
              Phone: formData.phone,
              Address: formData.address,
              HireDate: formData.dob,
              DriverPhoto: photoUrl,
              IsActive: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .select("DriverID");

          console.log("DriverNew - Drivers insert result:", { driversData, driversInsertErr });

          if (driversInsertErr)
            throw new Error("Error on Drivers table creation: " + driversInsertErr.message);

          // Use the numeric DriverID from the Drivers table
          const driverId = driversData[0].DriverID;
          console.log("DriverNew - Dispatching numeric driverId:", driverId, "type:", typeof driverId);
          dispatch({ type: "DRIVER_ID", payload: driverId });
          onSuccess();
        }
      } catch (error) {
        onError(error);
      } finally {
        setLoading(false);
      }
    },
    [driverID, driverDetail, profilePhoto, dispatch, onSuccess, onError]
  );

  const handleDelete = useCallback(async () => {
    if (!driverID) return;

    // Show confirmation dialog
    const confirmed = window.confirm(
      "Are you sure you want to delete this driver? This action cannot be undone and will remove the driver from all systems."
    );

    if (!confirmed) return;

    try {
      setLoading(true);

      // Delete from Drivers table first (to avoid foreign key constraints)
      const { error: driversDeleteErr } = await supabase
        .from("Drivers")
        .delete()
        .eq("auth_id", driverID);

      if (driversDeleteErr) {
        console.warn("Error deleting from Drivers table:", driversDeleteErr);
        // Continue with other deletions even if this fails
      }

      // Delete from driver_profiles table
      const { error: profilesDeleteErr } = await supabase
        .from("driver_profiles")
        .delete()
        .eq("auth_id", driverID);

      if (profilesDeleteErr) {
        console.warn(
          "Error deleting from driver_profiles table:",
          profilesDeleteErr
        );
        // Continue with other deletions even if this fails
      }

      // Delete from driver_auth table (this should be last)
      const { error: authDeleteErr } = await supabase
        .from("driver_auth")
        .delete()
        .eq("id", driverID);

      if (authDeleteErr) throw authDeleteErr;

      toast.success("Driver deleted successfully!");
      navigate("/driver-credentials");
    } catch (error) {
      console.error("Error deleting driver:", error);
      toast.error("Failed to delete driver. Check console for details.");
    } finally {
      setLoading(false);
    }
  }, [driverID, navigate]);

  const handleBack = () => {
    sessionStorage.getItem("UserRole") === "1"
      ? navigate("/super-admin")
      : navigate("/manager");
  };

  const handleClear = () => {
    reset({
      firstName: "",
      lastName: "",
      dob: "",
      phone: "",
      email: "",
      password: "",
      address: "",
    });
    setProfilePhoto(null);
    setPhotoPreview(null);
  };

  return (
    <div className="p-6 w-full">
      <HeaderContainer />
      {/* Top Section */}
      <div className="items-center mt-5 personal-info-content">
        <div
          onClick={handleBack}
          className="flex cursor-pointer justify-between items-center w-20 back-button"
        >
          <FaArrowLeft />
          <span>Back</span>
        </div>
        <p className="pt-4 font-semibold text-lg">Personal Information</p>
        <span>Update driver's photo and personal details here</span>

        <div className="w-100 mt-4 rounded-lg">
          <div className="flex justify-between mb-2">
            <div>
              <p>First Name</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="firstName"
                rules={{
                  required: "First name is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="mr-1 custom-input rounded-lg"
                    error={errors.firstName?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Last Name</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="lastName"
                rules={{
                  required: "Last name is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg"
                    error={errors.lastName?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Date of Birth</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="dob"
                rules={{
                  required: "Date of birth is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.dob?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Profile Photo</p>
            </div>
            <div className="flex justify-between w-35">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="custom-input rounded-lg"
              />
              {photoPreview && (
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="mt-2 rounded-lg"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                  }}
                />
              )}
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Phone Number</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="phone"
                rules={{
                  required: "Phone number is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="tel"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.phone?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Email Address</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="email"
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="email"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.email?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Password</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="password"
                rules={{
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="password"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.password?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Home Address</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="address"
                rules={{
                  required: "Address is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.address?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex gap-4 mt-4 save-button">
            <button
              disabled={loading}
              onClick={handleSubmit(handleSave)}
              className="flex-1 justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
            >
              {driverID ? "Update" : "Save Driver"}
            </button>
            <div
              onClick={handleClear}
              className="flex-1 justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
            >
              <span>Clear</span>
            </div>
            {driverID && (
              <button
                disabled={loading}
                onClick={handleDelete}
                className="flex-1 justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-red-button"
              >
                <span>Delete</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverDetail;
